import { useEffect } from 'react';
import { graphql, navigate } from 'gatsby';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import useInternalLinks from '@/hooks/useInternalLinks';
import pushInDataLayer from '@/utils/pushInDataLayer';
import errorLogger from '@/utils/errorLogger';
import countryList from '../constants/countryList';
import { FormFooter } from '../components/FormLayout/FormLayout';
import Input from '../components/Input/Input';
import Select from '../components/Select/Select';
import Textarea from '../components/Textarea/Textarea';
import Button from '../components/FormButton/FormButton';
import FormError from '../components/FormError/FormError';
import { schema } from './validation';
import configuration from './constants/config';
import useGetDataQueryParam from './hooks/useGetDataQueryParam';
import LegalText, { type LegalTextProps } from '../components/LegalText/LegalText';
import MarketingOptOut from '../components/MarketingOptOut/MarketingOptOut';
import { StyledForm, StyledHiddenInput } from './ProductContactForm.styles';
import useSetGeolocatedCountry from '../hooks/useSetGeolocatedCountry';
import useFormComplete from '../hooks/useFormComplete';
import { useLocation } from '@reach/router';

const ProductContactForm = ({
  system,
  productContactFormElements: {
    product,
    form_submit_label,
    success_redirect_page,
    options,
    legal_text,
  },
}: Queries.PRODUCT_CONTACT_FORMFragment) => {
  const { t } = useTranslation();
  const getInternalLinkURLByCodename = useInternalLinks();
  const initialValues = useGetDataQueryParam();

  const config = configuration[product.value];
  const redirectURL =
    getInternalLinkURLByCodename(success_redirect_page.value.system.codename) ?? '/';

  const hideLabel = options?.value?.includes('hide_labels') ?? false;

  const {
    control,
    watch,
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    shouldUnregister: true,
    defaultValues: {
      country: null,
    },
  });

  useSetGeolocatedCountry({ setValue, field: 'country', countryList });
  const isFormCompleteReady = useFormComplete({
    setValue,
    selectMappings: {
      country: countryList,
    },
  });
  const enhanceFormComplete = options?.value?.includes('enhance_form_complete') ?? false;
  const shouldHideFields = enhanceFormComplete && !isFormCompleteReady;

  useEffect(() => {
    if (initialValues) {
      setValue('name', decodeURIComponent(initialValues.name));
      setValue('email', initialValues.email);
      setValue('login', initialValues.login);

      // Auto-select first organization if available
      if (initialValues.organizations && initialValues.organizations.length > 0) {
        const firstOrg = initialValues.organizations[0];
        setValue('organization', {
          label: decodeURIComponent(firstOrg.name),
          value: firstOrg.key,
        });
      }
    }
  }, [initialValues, setValue]);

  const [category, login] = watch(['category', 'login']);
  const showExtraFields = category?.value === 'purchase_query' && (login ?? '').length === 0;
  const { pathname } = useLocation();

  const onSubmit = handleSubmit(async (values) => {
    try {
      const selectedOrg = initialValues?.organizations.find(
        (item) => item.key === values.organization
      );

      const submitValues = {
        ...values,
        subject: `[${values.category}] ${values.subject}`,
        ...(selectedOrg && {
          organization: selectedOrg.key,
          ...(selectedOrg.loc && { loc: selectedOrg.loc }),
          ...(selectedOrg.country && { country: selectedOrg.country }),
          ...(selectedOrg.status && { status: selectedOrg.status }),
        }),
      };

      const response = await fetch(config.action, {
        method: 'POST',
        body: JSON.stringify(submitValues),
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const responseBody = await response.json();

      if (!responseBody.ok) {
        throw new Error(responseBody.error);
      }

      pushInDataLayer({
        event: 'submittedForm',
        submittedFormData: {
          type: 'information',
          product,
          email: submitValues.email,
          fromPage: pathname,
          ...(submitValues.country && { country: submitValues.country }),
        },
      });

      navigate(redirectURL);
    } catch (error) {
      errorLogger(error);
      setError('root', { type: 'custom', message: 'Could not submit form' });
    }
  });

  const emailInput = (
    <Input
      type="email"
      id="email"
      placeholder="Email"
      required
      formGroupProps={{ label: 'Email', hideLabel }}
      {...register('email')}
      errors={errors}
      disabled={isSubmitting}
    />
  );

  return (
    <StyledForm noValidate onSubmit={onSubmit} id={system.codename}>
      {enhanceFormComplete && emailInput}
      <Input
        type="text"
        id="name"
        placeholder="Name"
        required
        formGroupProps={{ label: 'Name', hideLabel, isHidden: shouldHideFields }}
        {...register('name')}
        errors={errors}
        disabled={isSubmitting}
      />
      {!enhanceFormComplete && emailInput}
      <Controller
        control={control}
        name="category"
        render={({ field }) => (
          <Select
            required
            id="category"
            placeholder="Category"
            options={[
              { label: 'Purchase query', value: 'purchase_query' },
              { label: 'Other', value: 'other' },
            ]}
            formGroupProps={{ label: 'Category', hideLabel }}
            errors={errors}
            isDisabled={isSubmitting}
            {...field}
          />
        )}
      />
      {initialValues?.organizations && initialValues.organizations.length > 0 && (
        <Controller
          control={control}
          name="organization"
          render={({ field }) => (
            <Select
              id="organization"
              placeholder="Organization"
              options={initialValues.organizations.map((org) => ({
                label: decodeURIComponent(org.name),
                value: org.key,
              }))}
              errors={errors}
              formGroupProps={{ label: 'Organization', hideLabel }}
              isDisabled={isSubmitting}
              {...field}
            />
          )}
        />
      )}
      {showExtraFields && (
        <Controller
          control={control}
          name="country"
          render={({ field }) => (
            <Select
              id="country"
              placeholder="Select a country"
              options={countryList}
              formGroupProps={{ label: 'Country', hideLabel }}
              errors={errors}
              isDisabled={isSubmitting}
              {...field}
            />
          )}
        />
      )}
      {showExtraFields && (
        <Controller
          control={control}
          name="loc"
          render={({ field }) => (
            <Select
              id="loc"
              placeholder="Choose a value"
              options={['100k', '250k', '500k', '1M', '2M', '5M', '10M', '20M', '30M'].map(
                (item) => ({
                  value: item,
                  label: item,
                })
              )}
              formGroupProps={{ label: t('pricing.Lines of code'), hideLabel }}
              errors={errors}
              isDisabled={isSubmitting}
              {...field}
            />
          )}
        />
      )}
      <Input
        type="text"
        id="subject"
        placeholder="Message subject"
        formGroupProps={{ label: 'Subject', hideLabel }}
        {...register('subject')}
        errors={errors}
        disabled={isSubmitting}
      />
      <Textarea
        id="question"
        placeholder="How can we help?"
        required
        formGroupProps={{ label: 'Question', hideLabel }}
        {...register('question')}
        errors={errors}
        disabled={isSubmitting}
      />
      <MarketingOptOut
        label={t('form.Marketing Emails Opt Out')}
        errors={errors}
        register={register}
        disabled={isSubmitting}
      />
      {initialValues?.login && (
        <StyledHiddenInput
          type="hidden"
          id="login"
          formGroupProps={{ label: 'login', hideLabel: true }}
          {...register('login')}
        />
      )}
      <StyledHiddenInput
        type="hidden"
        id="_gotcha"
        formGroupProps={{ label: '_gotcha', hideLabel: true }}
        {...register('_gotcha')}
      />
      <FormFooter>
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {form_submit_label.value}
        </Button>
        <LegalText richText={legal_text as LegalTextProps['richText']} />
        {errors?.root?.message && <FormError>{errors.root.message}</FormError>}
      </FormFooter>
    </StyledForm>
  );
};

export const fragment = graphql`
  fragment PRODUCT_CONTACT_FORM on kontent_item_product_contact_form {
    __typename
    system {
      codename
    }
    productContactFormElements: elements {
      form_submit_label {
        value
      }
      legal_text {
        value
        links {
          link_id
          codename
        }
      }
      product {
        value
      }
      success_redirect_page {
        value {
          system {
            codename
          }
        }
      }
      options {
        value
      }
    }
  }
`;

export default ProductContactForm;
